import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  Icompany,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  Iitem,
  IitemDecoy,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { Item } from './item.define';

/**
 * Type alias for item decoy properties with specific generic types
 * @template T - Type for company (default: Icompany)
 * @template U - Type for item (default: Iitem)
 */
export type TitemDecoyProps = IitemDecoy<string, Icompany, Iitem>;

/**
 * Represents an item decoy in the system.
 * This class provides methods to manage item decoys, which are used to temporarily
 * hold items that may be replaced or modified later.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TitemDecoyProps - The type definition for item decoy properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link ItemDecoy} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const itemDecoy = await ItemDecoy.getAll();
 *
 * const itemDecoy = await ItemDecoy.filterAll({
 *
 * });
 *
 * const itemDecoy = await ItemDecoy.bulkDelete({
 *
 * });
 *
 * const itemDecoy = await ItemDecoy.getOne('resourceId');
 *
 * const itemDecoy = await ItemDecoy.remove();
 *
 * ```
 */
export class ItemDecoy extends DatabaseAuto {
  /** Base API endpoint for item decoy operations */
  static readonly baseUrl = '/item-decoy';

  /** Unique reference ID for the item decoy */
  readonly urId: TitemDecoyProps['urId'];

  /** Company associated with the item decoy */
  readonly company: TitemDecoyProps['company'];

  /** Array of items in the decoy */
  readonly items: Item[];

  /**
   * Creates a new instance of ItemDecoy.
   * @param {TitemDecoyProps} data - The item decoy data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TitemDecoyProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      items: {
        validate: (v) =>
          Array.isArray(v) &&
          v.every(item =>
            item !== null &&
            item !== undefined),
        message: 'Items must be a non-null array'
      }
    });

    super(data);

    this.urId = data.urId;
    this.company = data.company;

    // Create Item instances, defaulting to empty array if no items
    this.items = data.items.length > 0 ?
      data.items.map(val => new Item(val)) :
      [];
  }

  /**
   * Retrieves all item decoys with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: ItemDecoy[] }>}
   * An object containing the total count and an array of item decoys
   * @throws {Error} If there's an error fetching the item decoys
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TitemDecoyProps>>(`${ItemDecoy.baseUrl}?${query}`);

    const decoys = await lastValueFrom(observer$);

    return {
      count: decoys.count,
      dataArr: decoys.data.map(val => new ItemDecoy(val))
    };
  }

  /**
   * Filters item decoys based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: ItemDecoy[] }>}
   * An object containing the total count and an array of filtered item decoys
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TitemDecoyProps>>(
        `${ItemDecoy.baseUrl}/filter`,
        filter
      );

    const decoys = await lastValueFrom(observer$);

    return {
      count: decoys.count,
      dataArr: decoys.data.map(val => new ItemDecoy(val))
    };
  }

  /**
   * Retrieves a single item decoy by its ID.
   * @param {string} resourceId - The unique identifier of the item decoy to retrieve
   * @returns {Promise<ItemDecoy>} The requested item decoy
   * @throws {Error} If the item decoy is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TitemDecoyProps>(`${ItemDecoy.baseUrl}/${resourceId}`);
    const decoy = await lastValueFrom(observer$);

    return new ItemDecoy(decoy);
  }

  /**
   * Creates a new item decoy with the specified items.
   * @param {string[]} items - The IDs of the items to include in the decoy
   * @returns {Promise<ItemDecoy>} The newly created item decoy
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(items: string[]): Promise<ItemDecoy> {
    const observer$ = CommerceClient.ehttp
      .makePost<TitemDecoyProps>(
        `${ItemDecoy.baseUrl}`,
        { items }
      );

    return new ItemDecoy(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple item decoys based on the specified criteria.
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk deletion operation
   * @throws {Error} If the bulk deletion fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${ItemDecoy.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Deletes the current item decoy.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation
   * @throws {Error} If the deletion fails or the item decoy doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${ItemDecoy.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
